<template>
  <div class="plus-prompt-composer">
    <div v-if="localUploads.length" class="plus-prompt-composer__uploads px-5 pt-3 sm:px-6">
      <div class="flex items-center gap-2 overflow-x-auto overflow-y-visible pr-1 py-1">
        <div
          v-for="(u, idx) in localUploads"
          :key="u.url || idx"
          class="group relative h-[52px] w-[52px] shrink-0 overflow-visible rounded-2xl border border-white/10 bg-black/30"
          :title="u.name || (u.kind === 'video' ? '视频' : '图片')"
        >
          <div class="h-full w-full overflow-hidden rounded-2xl">
            <img
              v-if="u.kind === 'image'"
              :src="u.url"
              alt=""
              loading="lazy"
              class="h-full w-full object-cover opacity-95"
            />
            <div v-else class="relative h-full w-full">
              <video
                :src="u.url"
                muted
                playsinline
                preload="metadata"
                class="h-full w-full object-cover opacity-90"
              ></video>
              <span class="absolute inset-0 flex items-center justify-center text-white/70">
                <i class="fas fa-play-circle text-lg"></i>
              </span>
            </div>
          </div>
          <div
            class="absolute inset-0 bg-black/0 opacity-0 transition-colors group-hover:bg-black/55 group-hover:opacity-100"
          >
            <button
              type="button"
              class="absolute -right-2 -top-2 z-[2] flex h-6 w-6 items-center justify-center rounded-full border border-white/20 bg-black/70 text-white/90 shadow backdrop-blur-sm hover:bg-black/85"
              title="移除"
              @click.stop="removeLocalUpload(idx)"
            >
              <i class="fas fa-times text-[10px]"></i>
            </button>
            <button
              type="button"
              class="absolute left-1/2 top-1/2 h-7 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full border border-white/20 bg-white/15 px-3 text-[11px] font-semibold text-white hover:bg-white/25"
              @click.stop="insertReferenceUpload(u)"
            >
              引用
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="plus-prompt-composer__editor relative px-5 py-3 sm:px-6 sm:py-4">
      <div
        v-if="composerIsEmpty"
        class="pointer-events-none absolute left-5 top-3 text-[14px] leading-normal text-[rgba(130,128,150,0.88)] sm:left-6 sm:top-4"
      >
        输入你的想法或任务
      </div>
      <div
        ref="promptComposer"
        contenteditable="true"
        class="plus-prompt-composer__input w-full min-h-[5.5rem] bg-transparent text-[14px] leading-relaxed text-white/90 outline-none focus:outline-none whitespace-pre-wrap break-words"
        @input="onComposerInput"
        @keydown="onComposerKeydown"
        @paste="onComposerPaste"
      ></div>
    </div>

    <ComposerAtMentionPanel
      ref="atMentionPanel"
      :visible="atMentionOpen"
      :items="atMentionItems"
      :active-index="atMentionIndex"
      :query="atMentionQuery"
      :panel-style="atMentionPanelStyle"
      :loading="atMentionLoading"
      @pick="pickAtMentionItem"
      @hover-index="atMentionIndex = $event"
    />

    <div class="plus-prompt-toolbar">
      <div class="plus-prompt-toolbar__left">
        <button
          type="button"
          class="plus-prompt-chip plus-prompt-chip--round"
          title="上传图片或视频"
          @click="triggerUploadPick"
        >
          <i class="fas fa-plus text-[13px]"></i>
        </button>
        <input
          ref="uploadInput"
          type="file"
          tabindex="-1"
          class="plus-prompt-composer__file-input"
          accept="image/*,video/*"
          multiple
          @change="onUploadFilesSelected"
        />
        <PlusPromptWorkflowControls
          ref="workflowControls"
          v-model="workflowParams"
          :mode="launchTarget === 'canvas' ? 'canvas' : 'workflow'"
          @pick-asset="onAssetPicked"
          @reset="resetAll"
        />
      </div>
      <button
        type="button"
        class="plus-prompt-submit"
        :class="{ 'plus-prompt-submit--disabled': submitDisabled }"
        :disabled="submitDisabled"
        :title="launchTarget === 'canvas' ? '在画布创建节点并执行' : '进入对话工作流并发送'"
        @click="onSubmit"
      >
        <i class="fas fa-arrow-up text-xs"></i>
      </button>
    </div>
  </div>
</template>

<script>
import composerAtMentionMixin from '@/mixins/composerAtMentionMixin'
import PlusPromptWorkflowControls from '@/components/PlusPromptWorkflowControls.vue'
import { DEFAULT_WORKFLOW_MODEL_ID } from '@/constants/workflowModelOptions'
import { openLoginModal } from '@/utils/loginGate'
import { persistWorkflowUploadUrl } from '@/utils/workflowMediaPersist'
import {
  getComposerHtml,
  getComposerPlainText,
  handleComposerUploadKeydown,
  hasInlineUploadByUrl,
  insertUploadInlineAtCursor,
  isComposerEmpty,
  removeInlineUploadByUrl,
  rewriteInlineUploadUrl,
  syncReferencedUploadsFromComposer
} from '@/utils/workflowComposerInlineUpload'
import { saveWorkflowPromptHandoff } from '@/utils/workflowPromptHandoff'
import { saveCanvasNodeHandoff } from '@/utils/canvasNodeHandoff'
import { DEFAULT_CANVAS_LAUNCH_NODE_TYPE } from '@/constants/canvasLaunchNodeOptions'
import { showErrorToast, showSuccessToast } from '@/utils/toast'

export default {
  name: 'PlusPromptComposer',
  mixins: [composerAtMentionMixin],
  components: { PlusPromptWorkflowControls },
  props: {
    /** canvas：跳转节点画布并创建节点；workflow：对话工作流（默认） */
    launchTarget: {
      type: String,
      default: 'workflow'
    }
  },
  data() {
    return {
      atMentionComposerRef: 'promptComposer',
      contentPlain: '',
      contentHtml: '',
      referencedUploads: [],
      composerIsEmpty: true,
      workflowParams: {
        modelId: DEFAULT_WORKFLOW_MODEL_ID,
        model: null,
        nodeType: DEFAULT_CANVAS_LAUNCH_NODE_TYPE,
        nodeOption: null,
        style: null
      },
      localUploads: [],
      _localObjectUrls: []
    }
  },
  computed: {
    submitDisabled() {
      return isComposerEmpty(this.$refs.promptComposer, this.contentPlain)
    }
  },
  beforeDestroy() {
    this.revokeObjectUrls()
  },
  methods: {
    getComposerEl() {
      return this.$refs.promptComposer
    },
    onComposerInput() {
      const el = this.getComposerEl()
      this.contentPlain = getComposerPlainText(el)
      this.contentHtml = getComposerHtml(el)
      this.composerIsEmpty = isComposerEmpty(el, this.contentPlain)
      this.referencedUploads = syncReferencedUploadsFromComposer(el)
      this.syncAtMentionFromInput()
    },
    focusComposer() {
      const el = this.getComposerEl()
      if (el && el.focus) {
        this.$nextTick(() => {
          try {
            el.focus()
          } catch (e) {
            /* ignore */
          }
        })
      }
    },
    insertReferenceUpload(u) {
      if (!u || !u.url) return
      insertUploadInlineAtCursor(this.getComposerEl(), u)
      this.$nextTick(() => this.onComposerInput())
      this.focusComposer()
    },
    onComposerKeydown(e) {
      if (!e) return
      if (this.isAtMentionKeydownHandled(e)) return
      handleComposerUploadKeydown(e, this.getComposerEl(), () => this.onComposerInput())
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        this.onSubmit()
      }
    },
    onComposerPaste(e) {
      if (!e) return
      const cd = e.clipboardData
      if (!cd || !cd.items || !cd.items.length) return

      const items = Array.from(cd.items || [])
      const imgItems = items.filter(
        (it) => it && it.kind === 'file' && String(it.type || '').startsWith('image/')
      )
      if (!imgItems.length) return

      e.preventDefault()
      if (!Array.isArray(this.localUploads)) this.localUploads = []
      if (!Array.isArray(this._localObjectUrls)) this._localObjectUrls = []

      const added = []
      for (const it of imgItems) {
        const f = it.getAsFile ? it.getAsFile() : null
        if (!f) continue
        const ext = (String(f.type || '').split('/')[1] || 'png').replace(/[^a-z0-9]/gi, '')
        const name = `paste-${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext || 'png'}`
        const file = new File([f], name, { type: f.type || 'image/png' })
        const url = URL.createObjectURL(file)
        this._localObjectUrls.push(url)
        const u = { kind: 'image', url, name: file.name, type: file.type, size: file.size || 0 }
        this.localUploads.push(u)
        added.push(u)
        this.persistUploadInList(u).catch(() => {})
      }

      if (added.length) {
        showSuccessToast(`已粘贴 ${added.length} 张图片，点击「引用」插入输入框`)
      }
    },
    triggerUploadPick() {
      const el = this.$refs.uploadInput
      if (el && el.click) {
        try {
          el.click()
          return
        } catch (e) {
          /* ignore */
        }
      }
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*,video/*'
      input.multiple = true
      input.onchange = (evt) => this.onUploadFilesSelected(evt)
      try {
        input.click()
      } catch (e) {
        /* ignore */
      }
    },
    onUploadFilesSelected(e) {
      const files = Array.from(e && e.target && e.target.files ? e.target.files : [])
      if (e && e.target) e.target.value = ''
      if (!files.length) return

      if (!Array.isArray(this._localObjectUrls)) this._localObjectUrls = []
      if (!Array.isArray(this.localUploads)) this.localUploads = []

      const uploads = []
      for (const f of files) {
        if (!f) continue
        const type = String(f.type || '')
        const isImg = type.startsWith('image/')
        const isVid = type.startsWith('video/')
        if (!isImg && !isVid) continue
        const url = URL.createObjectURL(f)
        this._localObjectUrls.push(url)
        uploads.push({
          kind: isVid ? 'video' : 'image',
          url,
          name: f.name || '',
          type,
          size: f.size || 0
        })
      }

      if (!uploads.length) {
        showErrorToast('请选择图片或视频文件')
        return
      }

      this.localUploads = [...this.localUploads, ...uploads]
      showSuccessToast(`已添加 ${uploads.length} 个文件，点击「引用」插入输入框`)

      for (const u of uploads) {
        if (u && typeof u.url === 'string' && u.url.startsWith('blob:')) {
          this.persistUploadInList(u).catch(() => {})
        }
      }
    },
    async persistUploadInList(u) {
      if (!u || !u.url) return
      const oldUrl = String(u.url)
      try {
        const persisted = await persistWorkflowUploadUrl(oldUrl, u.name)
        if (persisted && persisted !== oldUrl) {
          const idx = this.localUploads.findIndex((x) => x && x.url === oldUrl)
          if (idx >= 0) {
            this.$set(this.localUploads, idx, { ...this.localUploads[idx], url: persisted })
          }
          u.url = persisted
          rewriteInlineUploadUrl(this.getComposerEl(), oldUrl, persisted)
          this.onComposerInput()
        }
      } catch (e) {
        /* ignore */
      }
    },
    removeLocalUpload(idx) {
      const i = Number(idx)
      if (!Number.isFinite(i)) return
      const list = [...(this.localUploads || [])]
      const removed = list.splice(i, 1)[0]
      this.localUploads = list
      const url = removed && removed.url ? String(removed.url) : ''
      if (url) {
        removeInlineUploadByUrl(this.getComposerEl(), url)
        this.onComposerInput()
      }
      if (url.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(url)
        } catch (e) {
          /* ignore */
        }
        this._localObjectUrls = (this._localObjectUrls || []).filter((x) => x !== url)
      }
    },
    onAssetPicked(a) {
      if (!a || !a.url) return
      const u = { kind: 'image', url: a.url, name: a.title || '未命名' }
      if (!Array.isArray(this.localUploads)) this.localUploads = []
      const exists = this.localUploads.some((x) => x && String(x.url) === String(u.url))
      if (exists) {
        showSuccessToast('该资产已在预览区')
        return
      }
      this.localUploads = [...this.localUploads, u]
      showSuccessToast('已加入素材预览，点击「引用」插入输入框')
    },
    revokeObjectUrls() {
      for (const url of this._localObjectUrls || []) {
        try {
          URL.revokeObjectURL(url)
        } catch (e) {
          /* ignore */
        }
      }
      this._localObjectUrls = []
    },
    resetAll() {
      this.closeAtMention()
      const el = this.getComposerEl()
      if (el) {
        try {
          el.innerHTML = ''
        } catch (e) {
          /* ignore */
        }
      }
      this.contentPlain = ''
      this.contentHtml = ''
      this.referencedUploads = []
      this.composerIsEmpty = true
      this.localUploads = []
      this.revokeObjectUrls()
      const ctrl = this.$refs.workflowControls
      if (ctrl && ctrl.resetWorkflow) ctrl.resetWorkflow()
    },
    async prepareReferencedUploadsForHandoff() {
      const refs = Array.isArray(this.referencedUploads) ? [...this.referencedUploads] : []
      const out = []
      for (const u of refs) {
        if (!u || !u.url) continue
        let url = String(u.url)
        if (!/^https?:\/\//i.test(url)) {
          try {
            url = await persistWorkflowUploadUrl(url, u.name)
          } catch (e) {
            /* keep original */
          }
        }
        out.push({
          kind: u.kind === 'video' ? 'video' : 'image',
          url,
          name: u.name || ''
        })
      }
      return out
    },
    async onSubmit() {
      if (this.submitDisabled) return

      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      if (!token) {
        openLoginModal()
        return
      }

      this.onComposerInput()
      const referencedUploads = await this.prepareReferencedUploadsForHandoff()
      const wp = this.workflowParams || {}
      const plain = (this.contentPlain || '').trim()

      if (this.launchTarget === 'canvas') {
        const nodeType = String(wp.nodeType || DEFAULT_CANVAS_LAUNCH_NODE_TYPE).trim()
        if (!plain) {
          showErrorToast('请先输入创作要求')
          return
        }
        const ok = saveCanvasNodeHandoff({
          nodeType,
          prompt: plain,
          contentPlain: plain,
          style: wp.style || null,
          autoRun: true
        })
        if (!ok) {
          showErrorToast('无法保存输入内容，请重试')
          return
        }
        this.$router
          .push({ path: '/workspace', query: { node: '1', handoff: '1' } })
          .catch(() => {})
        return
      }

      const ok = saveWorkflowPromptHandoff({
        prompt: plain,
        contentPlain: plain,
        contentHtml: this.contentHtml || '',
        modelId: wp.modelId || DEFAULT_WORKFLOW_MODEL_ID,
        style: wp.style || null,
        referencedUploads,
        autoSend: true
      })
      if (!ok) {
        showErrorToast('无法保存输入内容，请重试')
        return
      }

      this.$router
        .push({ name: 'ConversationWorkflow', query: { handoff: '1' } })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.plus-prompt-composer {
  display: flex;
  flex-direction: column;
  min-height: 0;
  position: relative;
  z-index: 1;
}

.plus-prompt-composer__file-input {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.plus-prompt-composer__input:empty {
  min-height: 5.5rem;
}

.plus-prompt-composer__input >>> [data-cw-upload='1'],
.plus-prompt-composer__input :deep([data-cw-upload='1']) {
  vertical-align: middle;
}

.plus-prompt-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(0, 0, 0, 0.14);
}

.plus-prompt-toolbar__left {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;
  overflow-x: auto;
  scrollbar-width: none;
}

.plus-prompt-toolbar__left::-webkit-scrollbar {
  display: none;
}

.plus-prompt-toolbar >>> .plus-prompt-chip,
.plus-prompt-chip {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(229, 231, 235, 0.95);
  transition: background 0.15s ease, border-color 0.15s ease;
}

.plus-prompt-toolbar >>> .plus-prompt-chip:hover,
.plus-prompt-chip:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.12);
}

.plus-prompt-toolbar >>> .plus-prompt-chip--round,
.plus-prompt-chip--round {
  height: 40px;
  width: 40px;
  border-radius: 9999px;
}

.plus-prompt-toolbar >>> .plus-prompt-chip--pill,
.plus-prompt-chip--pill {
  height: 40px;
  border-radius: 9999px;
  padding-left: 12px;
  padding-right: 14px;
  font-size: 12px;
  font-weight: 600;
}

.plus-prompt-submit {
  display: flex;
  height: 40px;
  width: 40px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  border: 1px solid var(--plus-submit-border, rgba(255, 255, 255, 0.14));
  background: var(
    --plus-submit-bg,
    linear-gradient(180deg, #4a3a6e 0%, #2d2248 100%)
  );
  color: var(--plus-submit-color, rgba(237, 233, 254, 0.96));
  box-shadow: var(--plus-submit-shadow, inset 0 1px 0 rgba(255, 255, 255, 0.06));
  transition:
    opacity 0.15s ease,
    transform 0.15s ease,
    box-shadow 0.15s ease,
    border-color 0.15s ease,
    background 0.15s ease,
    color 0.15s ease;
}

.plus-prompt-submit:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: rgba(196, 181, 253, 0.35);
  background: linear-gradient(180deg, #5a467e 0%, #362a58 100%);
  color: #fff;
  box-shadow: var(
    --plus-submit-shadow-hover,
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    0 0 16px rgba(109, 40, 217, 0.28)
  );
}

.plus-prompt-submit--disabled,
.plus-prompt-submit:disabled {
  cursor: not-allowed;
  opacity: 0.45;
  transform: none;
  box-shadow: none;
}
</style>
